import { authRepository } from '@/data/adapters/api/auth.api'
import { useSessionStore } from '@/lib/session.store'

/**
 * Spend the shared refresh cookie for an access token, once.
 *
 * The access token lives in `localStorage`, which is scoped per origin, so
 * someone who just signed in on the shell at the apex arrives here with
 * nothing. The `cc_refresh` cookie is the part that crosses — it is scoped to
 * `.commandcenterscc.com` — so it has to be spent before this app can conclude
 * anyone is signed out.
 *
 * `useSessionBootstrap` already does exactly this, but it is a component hook
 * in `__root.tsx`: it runs after the router has resolved `beforeLoad`, which is
 * where the guards live. A guard that decides first sends the visitor back to
 * the shell, the shell refreshes successfully and returns them here, and the
 * guard decides first again — the redirect loop. Guards await this instead.
 *
 * Single-flight: several guards can run for one navigation ($orgSlug and
 * $clientSlug both call it) and they should share one request, not race.
 */
let inflight: Promise<void> | null = null

export async function hydrateSessionFromSharedCookie(): Promise<void> {
  // No cookies on the server, and no localStorage to be missing either.
  if (typeof window === 'undefined') return
  if (useSessionStore.getState().token) return

  inflight ??= (async () => {
    try {
      const { token, user } = await authRepository.refresh()
      useSessionStore.getState().setSession(token, user)
    } catch {
      // No usable cookie. Staying unauthenticated is the honest outcome; the
      // caller decides where to send them.
    }
  })()

  await inflight
}

/** Test seam — the single-flight latch outlives a test otherwise. */
export function resetSessionHydrateForTests(): void {
  inflight = null
}
